/**
 * Origens de Lead - Catalogo hibrido (sistema + custom por tenant)
 *
 * RELEASE: 2026-07-10 v2.0.0
 *   - Adicionado registry dinamico em memoria (`registerDynamicLeadSources`)
 *     populado pelo hook `useLeadSources` a partir da RPC
 *     `get_tenant_lead_sources`. Isso permite que qualquer chamada existente
 *     a `getLeadSourceLabel(id)` / `getLeadSourceIcon(id)` continue funcionando
 *     e passe a resolver tambem origens customizadas do tenant sem exigir
 *     refactor em ~20 consumidores (badges, cards, filtros, etc.).
 *   - Origens de sistema (LEAD_SOURCES) permanecem como fallback estatico,
 *     garantindo comportamento offline e retrocompatibilidade.
 *   - Novo tipo `LeadSourceEntry` inclui `is_system` / `is_active`.
 *   - Novo helper `resolveLucideIconByName` para mapear nomes de icones
 *     escolhidos pelo cliente na tela de gestao.
 *
 * RELEASE: 2026-01-12 v1.1.0 - Lista fixa inicial + helpers.
 */
import {
  LucideIcon,
  Globe,
  Facebook,
  Instagram,
  MessageCircle,
  Search,
  Youtube,
  Video,
  Bot,
  BrainCircuit,
  Phone,
  Users,
  Building,
  Mail,
  Linkedin,
  Handshake,
  UserCheck,
  UsersRound,
  CalendarDays,
  MoreHorizontal,
  Tag,
  Star,
  Heart,
  Flag,
  Rocket,
  ShoppingCart,
  Megaphone,
  Newspaper,
  Radio,
  Tv,
  Store,
  Briefcase,
  Gift,
  Zap,
  Sparkles,
  Target,
} from 'lucide-react';

/**
 * Interface para definicao de origem de lead
 * (mantida por retrocompatibilidade com codigo antigo)
 */
export interface LeadSource {
  id: string;
  label: string;
  icon?: string; // legado (icofont)
}

/**
 * Entrada completa de origem retornada pelo backend
 */
export interface LeadSourceEntry {
  id: string;
  label: string;
  /** Nome do icone Lucide (ex: 'Globe', 'BrainCircuit') */
  iconName?: string | null;
  color?: string | null;
  is_system: boolean;
  is_active: boolean;
  sort_order: number;
}

/**
 * Mapeamento estatico de icones Lucide por ID de origem de SISTEMA.
 * Mantido para performance e como fallback caso o registry dinamico
 * ainda nao tenha sido populado.
 */
export const LEAD_SOURCE_ICONS: Record<string, LucideIcon> = {
  site: Globe,
  facebook: Facebook,
  instagram: Instagram,
  whatsapp: MessageCircle,
  google: Search,
  youtube: Youtube,
  tiktok: Video,
  chatbot: Bot,
  agente_ia: BrainCircuit,
  telefone: Phone,
  indicacao: Users,
  presencial: Building,
  eventos: CalendarDays,
  email: Mail,
  linkedin: Linkedin,
  parceiros: Handshake,
  representantes: UserCheck,
  colaboradores: UsersRound,
  outros: MoreHorizontal,
};

/**
 * Catalogo de icones Lucide DISPONIVEIS para o cliente escolher
 * ao criar uma origem custom na tela de gestao.
 * Chave = nome (case-sensitive) armazenado no banco em `icon`.
 */
export const LEAD_SOURCE_ICON_LIBRARY: Record<string, LucideIcon> = {
  Globe,
  Facebook,
  Instagram,
  MessageCircle,
  Search,
  Youtube,
  Video,
  Bot,
  BrainCircuit,
  Phone,
  Users,
  Building,
  Mail,
  Linkedin,
  Handshake,
  UserCheck,
  UsersRound,
  CalendarDays,
  MoreHorizontal,
  Tag,
  Star,
  Heart,
  Flag,
  Rocket,
  ShoppingCart,
  Megaphone,
  Newspaper,
  Radio,
  Tv,
  Store,
  Briefcase,
  Gift,
  Zap,
  Sparkles,
  Target,
};

/**
 * Lista padronizada de origens de SISTEMA (fallback estatico).
 * O catalogo completo (sistema + custom) vem via useLeadSources().
 */
export const LEAD_SOURCES: LeadSource[] = [
  { id: 'site', label: 'Site' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'google', label: 'Google' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'chatbot', label: 'ChatBot' },
  { id: 'agente_ia', label: 'Agente de IA' },
  { id: 'telefone', label: 'Telefone' },
  { id: 'indicacao', label: 'Indicação' },
  { id: 'presencial', label: 'Presencial' },
  { id: 'eventos', label: 'Eventos' },
  { id: 'email', label: 'Email' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'parceiros', label: 'Parceiros' },
  { id: 'representantes', label: 'Representantes' },
  { id: 'colaboradores', label: 'Colaboradores' },
  { id: 'outros', label: 'Outros' },
];

// ---------------------------------------------------------------------------
// Registry dinamico em memoria (populado pelo hook useLeadSources)
// ---------------------------------------------------------------------------
type DynamicEntry = { label: string; iconName?: string | null };
const _dynamicRegistry = new Map<string, DynamicEntry>();

/**
 * Popula o registry dinamico com as origens (sistema + custom) vindas da RPC.
 * Chamado automaticamente pelo hook `useLeadSources` sempre que os dados
 * carregam ou mudam. Componentes que consomem apenas `getLeadSourceLabel(id)`
 * passam a resolver origens custom sem alteracao de codigo.
 */
export function registerDynamicLeadSources(entries: LeadSourceEntry[]): void {
  _dynamicRegistry.clear();
  for (const e of entries) {
    _dynamicRegistry.set(e.id, { label: e.label, iconName: e.iconName ?? null });
  }
}

/**
 * Resolve um componente de icone Lucide pelo nome armazenado no banco.
 */
export function resolveLucideIconByName(name?: string | null): LucideIcon | null {
  if (!name) return null;
  return LEAD_SOURCE_ICON_LIBRARY[name] ?? null;
}

/**
 * Retorna o componente Lucide para uma origem.
 * Prioridade: registry dinamico -> mapa estatico -> null.
 */
export function getLeadSourceIcon(sourceId: string | null | undefined): LucideIcon | null {
  if (!sourceId) return null;
  const dyn = _dynamicRegistry.get(sourceId);
  if (dyn?.iconName) {
    const icon = resolveLucideIconByName(dyn.iconName);
    if (icon) return icon;
  }
  return LEAD_SOURCE_ICONS[sourceId] ?? null;
}

/**
 * Busca o label da origem pelo id.
 * Prioridade: registry dinamico -> lista estatica -> id original (fallback).
 */
export function getLeadSourceLabel(sourceId: string | null | undefined): string {
  if (!sourceId) return '-';
  const dyn = _dynamicRegistry.get(sourceId);
  if (dyn) return dyn.label;
  const source = LEAD_SOURCES.find(s => s.id === sourceId);
  return source?.label || sourceId;
}

/**
 * Busca a origem completa pelo id (legado - retorna estrutura antiga).
 */
export function getLeadSource(sourceId: string | null | undefined): LeadSource | null {
  if (!sourceId) return null;
  const dyn = _dynamicRegistry.get(sourceId);
  if (dyn) return { id: sourceId, label: dyn.label };
  return LEAD_SOURCES.find(s => s.id === sourceId) || null;
}

/**
 * Verifica se um valor esta no catalogo (sistema OU custom carregado).
 */
export function isValidLeadSource(sourceId: string): boolean {
  if (_dynamicRegistry.has(sourceId)) return true;
  return LEAD_SOURCES.some(s => s.id === sourceId);
}

/**
 * Retorna lista de IDs conhecidos (uniao registry + fallback estatico).
 */
export function getLeadSourceIds(): string[] {
  const ids = new Set<string>();
  for (const id of _dynamicRegistry.keys()) ids.add(id);
  for (const s of LEAD_SOURCES) ids.add(s.id);
  return Array.from(ids);
}
